import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TaskQuantityRow, Uom } from '../../manager/types';
import {
  deleteTaskQuantity,
  listTaskQuantities,
  putTaskQuantity,
} from '../../../../services/epcProjectService';

const KEY = (projectId: string) => ['epc', 'task-quantities', projectId];

/** Scope column data + PMO save/clear mutations for the schedule panel. */
export function useTaskQuantities(projectId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: KEY(projectId),
    enabled: !!projectId,
    queryFn: () => listTaskQuantities(projectId),
  });

  const byTaskKey = useMemo(() => {
    const m = new Map<string, TaskQuantityRow>();
    for (const row of query.data ?? []) m.set(row.taskKey, row);
    return m;
  }, [query.data]);

  const save = useMutation({
    mutationFn: ({ taskKey, uom, scopeQty }: { taskKey: string; uom: Uom; scopeQty: number }) =>
      putTaskQuantity(projectId, taskKey, { uom, scopeQty }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });

  const remove = useMutation({
    mutationFn: (taskKey: string) => deleteTaskQuantity(projectId, taskKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });

  return { query, byTaskKey, save, remove };
}
